import React, { useEffect, useRef, useState } from 'react';
import { featuredProjects, githubProjects, personalInfo } from '../data/mock';
import { ExternalLink, Code, Layers, Server, FolderGit2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

// Animated gradient underline component
const AnimatedUnderline = ({ isVisible, delay = 0, dark = false }) => (
  <div className="relative h-1 mt-4 overflow-hidden">
    <div
      className={`absolute inset-0 transition-all duration-1000 ${dark
        ? 'bg-gradient-to-r from-white via-gray-400 to-gray-600'
        : 'bg-gradient-to-r from-gray-800 via-gray-400 to-gray-200 dark:from-white dark:via-gray-400 dark:to-gray-700'
        } ${isVisible ? 'w-32 opacity-100' : 'w-0 opacity-0'}`}
      style={{ transitionDelay: `${delay}ms` }}
    />
    {/* Animated shimmer overlay */}
    <div
      className={`absolute inset-0 w-32 overflow-hidden ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ transitionDelay: `${delay + 500}ms` }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
    </div>
  </div>
);

// Individual project card component with its own animation state
const ProjectCard = ({ project, isVisible, cardIndex }) => {
  const [hoveredTech, setHoveredTech] = useState(null);
  const [activeHighlight, setActiveHighlight] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    const totalItems = project.highlights.length + project.technical.length;
    const interval = setInterval(() => {
      setActiveHighlight(prev => (prev + 1) % totalItems);
    }, 3000);
    return () => clearInterval(interval);
  }, [isVisible, project.highlights.length, project.technical.length]);

  return (
    <div
      className={`relative bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 rounded-3xl p-8 md:p-12 transition-all duration-700 delay-200 border border-gray-800 hover:border-gray-700 group opacity-60 hover:opacity-70 ${isVisible ? 'translate-y-0' : 'translate-y-8'
        }`}
    >
      {/* Animated corner accent */}
      <div className="absolute top-0 right-0 w-32 h-32 overflow-hidden rounded-tr-3xl">
        <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-gray-700/50 to-transparent animate-pulse-slow" />
      </div>

      {/* Project Title with animation */}
      <div className="relative mb-8">
        <h3 className={`text-2xl md:text-3xl font-light text-white mb-2 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {project.title}
        </h3>
        {project.repoUrl && (
          <a
            href={project.repoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors duration-300 mb-4"
          >
            <ExternalLink size={14} />
            {project.repoUrl.replace('https://github.com/', 'github.com/')}
          </a>
        )}
        <div className={`text-base text-gray-400 font-light leading-relaxed transition-all duration-500 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {project.overview.split('\n\n').map((paragraph, index) => (
            <p key={index} className={index > 0 ? 'mt-4' : ''}>
              {paragraph}
            </p>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-10">
        {/* What I Built - with animated list */}
        <div className={`p-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 transition-all duration-500 delay-200 hover:border-gray-300 dark:hover:border-gray-600 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
          <div className="flex items-center gap-2 mb-4">
            <Layers size={18} className="text-gray-500 dark:text-gray-500" />
            <h4 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-gray-500">What I Built</h4>
          </div>
          <ul className="space-y-3">
            {project.highlights.map((item, index) => (
              <li
                key={index}
                className={`text-sm font-light flex items-start gap-3 transition-all duration-500 ${activeHighlight === index
                  ? 'text-black dark:text-white translate-x-2'
                  : 'text-gray-600 dark:text-gray-400'
                  }`}
                onMouseEnter={() => setActiveHighlight(index)}
              >
                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 transition-all duration-300 ${activeHighlight === index ? 'bg-black dark:bg-white scale-125' : 'bg-gray-400 dark:bg-gray-600'
                  }`} />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Technical Highlights */}
        <div className={`p-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 transition-all duration-500 delay-300 hover:border-gray-300 dark:hover:border-gray-600 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
          <div className="flex items-center gap-2 mb-4">
            <Server size={18} className="text-gray-500 dark:text-gray-500" />
            <h4 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-gray-500">Technical Highlights</h4>
          </div>
          <ul className="space-y-3">
            {project.technical.map((item, index) => (
              <li
                key={index}
                className={`text-sm font-light flex items-start gap-3 transition-all duration-500 ${activeHighlight === (project.highlights.length + index)
                  ? 'text-black dark:text-white translate-x-2'
                  : 'text-gray-600 dark:text-gray-400'
                  }`}
                onMouseEnter={() => setActiveHighlight(project.highlights.length + index)}
              >
                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 transition-all duration-300 ${activeHighlight === (project.highlights.length + index) ? 'bg-black dark:bg-white scale-125' : 'bg-gray-400 dark:bg-gray-600'
                  }`} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tech Stack with hover effects */}
      <div className={`mb-10 transition-all duration-500 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="flex items-center gap-2 mb-4">
          <Code size={18} className="text-gray-500" />
          <h4 className="text-sm font-medium uppercase tracking-wider text-gray-500">Tech Stack</h4>
        </div>
        <div className="flex flex-wrap gap-2">
          {project.techStack.map((tech, index) => (
            <Badge
              key={tech}
              variant="outline"
              className={`text-sm font-light border-gray-700 transition-all duration-300 cursor-default ${hoveredTech === index
                ? 'bg-white text-black border-white scale-110'
                : 'text-gray-400 hover:border-gray-500'
                }`}
              onMouseEnter={() => setHoveredTech(index)}
              onMouseLeave={() => setHoveredTech(null)}
              style={{ transitionDelay: `${index * 30}ms` }}
            >
              {tech}
            </Badge>
          ))}
        </div>
      </div>

      {/* CTA Buttons */}
      <div className={`flex flex-wrap gap-4 transition-all duration-500 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {project.links.map((link, index) => (
          <Button
            key={link.url}
            variant="outline"
            className={`border-white/80 text-white hover:bg-white hover:text-black rounded-full px-6 transition-all duration-300 hover:scale-105 ${index === 0 ? 'bg-white/10' : ''
              }`}
            onClick={() => window.open(link.url, '_blank')}
          >
            <ExternalLink className="mr-2 h-4 w-4" />{link.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

const FeaturedProject = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [githubVisible, setGithubVisible] = useState(false);
  const sectionRef = useRef(null);
  const githubRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const githubObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setGithubVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (githubRef.current) githubObserver.observe(githubRef.current);

    return () => {
      observer.disconnect();
      githubObserver.disconnect();
    };
  }, []);

  return (
    <>
      {/* Featured Projects Section */}
      <section
        id="projects"
        ref={sectionRef}
        className="py-14 md:py-20 bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 transition-colors duration-500 relative overflow-hidden"
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-gray-200/35 via-gray-100/20 to-transparent dark:from-gray-700/25 dark:via-gray-800/15 rounded-full blur-3xl animate-morph opacity-40" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-gray-300/30 to-transparent dark:from-gray-600/20 rounded-full blur-3xl animate-morph-reverse opacity-30" />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          {/* Section Title */}
          <div className="mb-16">
            <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight text-black dark:text-white">
                Featured Projects
              </h2>
            </div>
          </div>

          {/* Project Cards */}
          <div className="space-y-12">
            {featuredProjects.map((project, index) => (
              <ProjectCard
                key={project.title}
                project={project}
                isVisible={isVisible}
                cardIndex={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* GitHub Projects Section - Light Grey Theme */}
      <section
        ref={githubRef}
        className="py-14 md:py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-500 relative overflow-hidden"
      >
        {/* Animated background decorations */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-gray-200/35 via-gray-100/20 to-transparent dark:from-gray-700/25 dark:via-gray-800/15 rounded-full blur-3xl animate-morph opacity-40" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-gray-300/30 to-transparent dark:from-gray-600/20 rounded-full blur-3xl animate-morph-reverse opacity-30" />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          {/* Section Header */}
          <div className="mb-12">
            <div className={`transition-all duration-700 ${githubVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <h3 className="text-3xl md:text-4xl font-extralight text-black dark:text-white mb-2">Other Projects</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-light">Personal & Learning Projects</p>
            </div>
            <AnimatedUnderline isVisible={githubVisible} delay={200} />
          </div>

          {/* View GitHub button */}
          <div className={`mb-8 transition-all duration-700 delay-100 ${githubVisible ? 'opacity-100' : 'opacity-0'}`}>
            <Button
              variant="outline"
              className="border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black hover:border-black dark:hover:border-white rounded-full transition-all duration-300 hover:scale-105"
              onClick={() => window.open(personalInfo.github, '_blank')}
            >
              <ExternalLink size={16} className="mr-2" />View All on GitHub
            </Button>
          </div>

          {/* Project Cards Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {githubProjects.map((project, index) => (
              <div
                key={project.title}
                className={`group bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-xl p-6 transition-all duration-500 hover:border-gray-400 dark:hover:border-gray-600 hover:-translate-y-2 hover:shadow-2xl ${githubVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-black dark:group-hover:bg-white transition-colors">
                    <FolderGit2 size={20} className="text-gray-600 dark:text-gray-400 group-hover:text-white dark:group-hover:text-black transition-colors" />
                  </div>
                  <h4 className="text-lg font-light text-black dark:text-white">{project.title}</h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 font-light leading-relaxed mb-4 line-clamp-3">{project.description}</p>
                <div className="flex flex-wrap gap-2">
                  {project.techStack.map((tech) => (
                    <span key={tech} className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800/50 px-2 py-1 rounded group-hover:text-black dark:group-hover:text-white transition-colors">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default FeaturedProject;
